import { Timestamp, getDocs, writeBatch } from 'firebase/firestore'
import { firebase } from '../lib/firebase'
import {
  appSettingsRef,
  blockRef,
  blocksRef,
  iterationsRef,
  periodNoteRef,
  taxonomyRef,
  trackerDayRef,
  trackerDaysRef,
  trackersRef,
} from './paths'
import { toBlock } from './blocks'
import { normaliseTrackerDay, normaliseTrackers, trackerDayId } from './trackers'
import type { AppSettings, Block, PeriodNote, Taxonomy } from './types'
import type { IterationConfig } from './iterations'
import type { Tracker, TrackerDay } from './trackers'
import { collection, getDoc } from 'firebase/firestore'

export const BACKUP_VERSION = 2

export interface Backup {
  version: number
  exportedAt: string
  blocks: Block[]
  taxonomy: Taxonomy | null
  settings: AppSettings | null
  iterations: IterationConfig | null
  periodNotes: Array<{ id: string } & Omit<PeriodNote, 'updatedAt'>>
  /** Absent in version 1 files, which predate trackers. */
  trackers: Tracker[]
  trackerDays: TrackerDay[]
}

/** Everything this account owns, as plain JSON with instants as epoch ms. */
export async function buildBackup(uid: string): Promise<Backup> {
  const [blockDocs, taxonomyDoc, settingsDoc, iterationsDoc, noteDocs, trackerDoc, trackerDayDocs] =
    await Promise.all([
      getDocs(blocksRef(uid)),
      getDoc(taxonomyRef(uid)),
      getDoc(appSettingsRef(uid)),
      getDoc(iterationsRef(uid)),
      getDocs(collection(firebase().db, 'users', uid, 'periodNotes')),
      getDoc(trackersRef(uid)),
      getDocs(trackerDaysRef(uid)),
    ])

  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    blocks: blockDocs.docs.map(toBlock).sort((a, b) => a.start - b.start),
    taxonomy: taxonomyDoc.exists() ? (taxonomyDoc.data() as Taxonomy) : null,
    settings: settingsDoc.exists() ? (settingsDoc.data() as AppSettings) : null,
    iterations: iterationsDoc.exists() ? (iterationsDoc.data() as IterationConfig) : null,
    periodNotes: noteDocs.docs.map((doc) => {
      const data = doc.data() as PeriodNote
      return {
        id: doc.id,
        scope: data.scope,
        key: data.key,
        note: data.note ?? null,
        tags: data.tags ?? [],
      }
    }),
    trackers: normaliseTrackers(trackerDoc.data()),
    trackerDays: trackerDayDocs.docs
      .map((doc) => normaliseTrackerDay(doc.data()))
      .filter((day): day is TrackerDay => day !== null)
      .sort((a, b) => (a.day < b.day ? -1 : 1)),
  }
}

export class BackupError extends Error {}

/**
 * Parsing is separate from restoring, and strict: a malformed file should fail
 * before anything is written, not halfway through.
 */
export function parseBackup(text: string): Backup {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    throw new BackupError('That file is not valid JSON.')
  }
  if (typeof raw !== 'object' || raw === null) throw new BackupError('That file is not a backup.')

  const candidate = raw as Partial<Backup>
  if (typeof candidate.version !== 'number') throw new BackupError('That file is not a backup.')
  if (candidate.version > BACKUP_VERSION) {
    throw new BackupError(`That backup is version ${candidate.version}; this app reads ${BACKUP_VERSION}.`)
  }
  if (!Array.isArray(candidate.blocks)) throw new BackupError('That backup has no blocks.')

  const blocks: Block[] = candidate.blocks.map((block, index) => {
    const where = `Block ${index + 1}`
    if (!block || typeof block !== 'object') throw new BackupError(`${where} is not an object.`)
    const { id, start, end, categoryId } = block as Block
    if (typeof id !== 'string' || !id) throw new BackupError(`${where} has no id.`)
    if (typeof start !== 'number' || !Number.isFinite(start)) {
      throw new BackupError(`${where} has no start time.`)
    }
    if (end !== null && (typeof end !== 'number' || !Number.isFinite(end))) {
      throw new BackupError(`${where} has an unreadable end time.`)
    }
    if (end !== null && end <= start) throw new BackupError(`${where} ends before it starts.`)
    if (typeof categoryId !== 'string' || !categoryId) {
      throw new BackupError(`${where} has no category.`)
    }
    return {
      id,
      start,
      end,
      categoryId,
      note: (block as Block).note ?? null,
      tags: Array.isArray((block as Block).tags) ? (block as Block).tags : [],
      source: (block as Block).source ?? 'manual',
    }
  })

  const open = blocks.filter((block) => block.end === null)
  if (open.length > 1) throw new BackupError('That backup has more than one running block.')

  return {
    version: candidate.version,
    exportedAt: typeof candidate.exportedAt === 'string' ? candidate.exportedAt : '',
    blocks,
    taxonomy: candidate.taxonomy ?? null,
    settings: candidate.settings ?? null,
    iterations: candidate.iterations ?? null,
    periodNotes: Array.isArray(candidate.periodNotes) ? candidate.periodNotes : [],
    trackers: normaliseTrackers(candidate.trackers),
    trackerDays: (Array.isArray(candidate.trackerDays) ? candidate.trackerDays : [])
      .map(normaliseTrackerDay)
      .filter((day): day is TrackerDay => day !== null),
  }
}

const BATCH_LIMIT = 400

export interface RestoreResult {
  blocks: number
  periodNotes: number
  trackerDays: number
  taxonomy: boolean
  settings: boolean
}

/**
 * Restores by id, so re-importing the same file twice is a no-op rather than a
 * second copy of every block. Blocks already present under a different id are
 * left alone — this restores, it does not reconcile.
 */
export async function restoreBackup(uid: string, backup: Backup): Promise<RestoreResult> {
  const db = firebase().db
  for (let i = 0; i < backup.blocks.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db)
    for (const block of backup.blocks.slice(i, i + BATCH_LIMIT)) {
      const { id, start, end, ...rest } = block
      batch.set(blockRef(uid, id), {
        ...rest,
        start: Timestamp.fromMillis(start),
        end: end === null ? null : Timestamp.fromMillis(end),
        createdAt: Timestamp.fromMillis(start),
        updatedAt: Timestamp.now(),
      } as never)
    }
    await batch.commit()
  }

  if (backup.periodNotes.length > 0) {
    const batch = writeBatch(db)
    for (const note of backup.periodNotes.slice(0, BATCH_LIMIT)) {
      batch.set(periodNoteRef(uid, note.id), {
        scope: note.scope,
        key: note.key,
        note: note.note ?? null,
        tags: note.tags ?? [],
        updatedAt: Timestamp.now(),
      } as never)
    }
    await batch.commit()
  }

  for (let i = 0; i < backup.trackerDays.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db)
    for (const day of backup.trackerDays.slice(i, i + BATCH_LIMIT)) {
      batch.set(trackerDayRef(uid, trackerDayId(day.trackerId, day.day)), day as never)
    }
    await batch.commit()
  }

  const settingsBatch = writeBatch(db)
  if (backup.trackers.length > 0) settingsBatch.set(trackersRef(uid), { trackers: backup.trackers })
  if (backup.taxonomy) settingsBatch.set(taxonomyRef(uid), backup.taxonomy)
  if (backup.settings) settingsBatch.set(appSettingsRef(uid), backup.settings)
  if (backup.iterations) settingsBatch.set(iterationsRef(uid), backup.iterations)
  await settingsBatch.commit()

  return {
    blocks: backup.blocks.length,
    periodNotes: backup.periodNotes.length,
    trackerDays: backup.trackerDays.length,
    taxonomy: backup.taxonomy !== null,
    settings: backup.settings !== null,
  }
}

/** Hands the file to the browser. Revoked on the next tick, not left dangling. */
export function downloadBackup(backup: Backup): void {
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `timegrid-${backup.exportedAt.slice(0, 10)}.json`
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
